import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  PauseIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline';
import {
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import Loader from '../../components/Loader/Loader';
import Avatar from '../../components/Avatar/Avatar';
import EmptyState from '../../components/EmptyState/EmptyState';
import Page from '../../components/Page/Page';
import { useDismissableSnackbar } from '../../hooks/useDismissableSnackbar';
import { timeSinceString } from '../../util/helpers';

import { REEL_COMMENTS, REEL_WITH_COMMENTS } from '../../graphql/queries-gql';
import { DELETE_REEL_COMMENT } from '../../graphql/mutations-gql';

import {
  DeleteConfirmationOverlay,
  DeleteConfirmationContent,
  DeleteConfirmationButtons,
  ConfirmButton,
  VideoContainer,
  ReelVideo,
  VideoControls,
  VideoTimeline,
  VideoProgress,
  VideoControlsRow,
  PlayPauseButton,
  TimeDisplay,
} from '../reels/styled';

import {
  PageContainer,
  CommentListPanel,
  DetailPanel,
  PaneScroll,
  PaneFooter,
  PaneState,
  PaneTitle,
  DetailBody,
  DetailHeader,
  VideoWrap,
  ReelThumb,
  UserCell,
  UserName,
  CommentText,
  CommentMeta,
  ReelDescription,
  SectionTitle,
  DetailCommentList,
  DetailCommentRow,
  DetailCommentBody,
  DetailCommentHead,
  DetailCommentText,
  ConfirmTitle,
  ConfirmText,
} from './styled';

const PAGE_SIZE = 25;

interface ReelComment {
  _id: string;
  reelId: string;
  reelThumbnailUrl: string;
  byUserId: string;
  byUsername: string;
  byUserProfilePhotoUrl: string;
  text: string;
  created: string;
}

interface ReelWithComments {
  reel: {
    _id: string;
    url: string;
    thumbnailUrl: string;
    thumbnailBlurhash: string;
    uploadId: string;
    byUserId: string;
    description: string;
    profilePhotoUrl: string;
    tags: string[];
  };
  comments: ReelComment[];
}

const ReelComments = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useDismissableSnackbar();

  const [comments, setComments] = useState<ReelComment[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedComment, setSelectedComment] = useState<ReelComment | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [fetchComments, { loading, error }] = useLazyQuery(REEL_COMMENTS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setComments(data.reelComments.nodes);
      setTotalPages(data.reelComments.paging.totalPages);
    },
  });

  const { data: detailData, loading: detailLoading } = useQuery<{
    reelWithComments: ReelWithComments;
  }>(REEL_WITH_COMMENTS, {
    variables: { reelId: selectedComment?.reelId },
    skip: !selectedComment,
    fetchPolicy: 'network-only',
  });

  const [deleteComment] = useMutation(DELETE_REEL_COMMENT);

  useEffect(() => {
    fetchComments({
      variables: {
        paging: { type: 'page', pageArgs: { page: 1, limit: PAGE_SIZE } },
      },
    });
  }, [fetchComments]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchComments({
      variables: {
        paging: { type: 'page', pageArgs: { page: value, limit: PAGE_SIZE } },
      },
    });
  };

  const handleCommentClick = (comment: ReelComment) => {
    setSelectedComment(comment);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleCloseDetail = () => {
    setSelectedComment(null);
    setIsPlaying(false);
  };

  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (commentToDelete) {
      deleteComment({
        variables: { commentId: commentToDelete },
        onCompleted: () => {
          showSnackbar('Comment deleted', 'success');
          setComments((prev) => prev.filter((c) => c._id !== commentToDelete));
          if (selectedComment?._id === commentToDelete) {
            setSelectedComment(null);
          }
          setShowDeleteConfirm(false);
          setCommentToDelete(null);
        },
        onError: (err) => {
          showSnackbar('Failed to delete comment: ' + err.message, 'error');
          setShowDeleteConfirm(false);
          setCommentToDelete(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCommentToDelete(null);
  };

  // Video controls
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const reelDetail = detailData?.reelWithComments;
  const hasDetail = !!selectedComment;

  return (
    <Page title="Reel comments" fill>
      <PageContainer>
        <CommentListPanel hasDetail={hasDetail}>
          <PaneScroll>
            {loading && (
              <PaneState>
                <Loader />
              </PaneState>
            )}
            {error && (
              <PaneState>
                <ErrorAlert error={error} />
              </PaneState>
            )}

            {!loading && comments.length > 0 && (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>Reel</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comments.map((comment) => (
                    <TableRow
                      key={comment._id}
                      hover
                      selected={selectedComment?._id === comment._id}
                      onClick={() => handleCommentClick(comment)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ width: 60, padding: '8px' }}>
                        <ReelThumb src={comment.reelThumbnailUrl} alt="" />
                      </TableCell>
                      <TableCell>
                        <UserCell>
                          <Avatar
                            url={comment.byUserProfilePhotoUrl}
                            name={comment.byUsername}
                            sx={{ width: 32, height: 32 }}
                            onClick={() => navigate(`/user/${comment.byUserId}`)}
                          />
                          <UserName
                            type="button"
                            title="Open user"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              navigate(`/user/${comment.byUserId}`);
                            }}
                          >
                            {comment.byUsername}
                          </UserName>
                        </UserCell>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 0, width: '50%' }}>
                        <CommentText title={comment.text}>{comment.text}</CommentText>
                      </TableCell>
                      <TableCell>
                        <CommentMeta>{timeSinceString(comment.created)} ago</CommentMeta>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete comment">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete comment"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(comment._id);
                            }}
                          >
                            <TrashIcon width={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!loading && comments.length === 0 && (
              <PaneState>
                <EmptyState
                  Icon={ChatBubbleOvalLeftEllipsisIcon}
                  title="No comments found"
                  style={{ width: '100%' }}
                />
              </PaneState>
            )}
          </PaneScroll>

          {totalPages > 1 && (
            <PaneFooter>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="small"
              />
            </PaneFooter>
          )}
        </CommentListPanel>

        {hasDetail && (
          <DetailPanel>
            <DetailHeader>
              <PaneTitle>Reel</PaneTitle>
              <Tooltip title="Close">
                <IconButton
                  onClick={handleCloseDetail}
                  size="small"
                  aria-label="Close reel"
                >
                  <XMarkIcon width={18} />
                </IconButton>
              </Tooltip>
            </DetailHeader>

            <PaneScroll>
              <DetailBody>
                {detailLoading && (
                  <PaneState>
                    <Loader />
                  </PaneState>
                )}

                {reelDetail && (
                  <>
                    <VideoWrap>
                      <VideoContainer>
                        <ReelVideo
                          ref={videoRef}
                          src={reelDetail.reel.url ?? ''}
                          onEnded={handleVideoEnd}
                          onClick={handlePlayPause}
                          onTimeUpdate={handleTimeUpdate}
                          onLoadedMetadata={handleLoadedMetadata}
                          playsInline
                        />
                        <VideoControls>
                          <VideoTimeline onClick={handleTimelineClick}>
                            <VideoProgress progress={(currentTime / duration) * 100} />
                          </VideoTimeline>
                          <VideoControlsRow>
                            <PlayPauseButton
                              onClick={handlePlayPause}
                              aria-label={isPlaying ? 'Pause' : 'Play'}
                              title={isPlaying ? 'Pause' : 'Play'}
                            >
                              {isPlaying ? <PauseIcon /> : <PlayIcon />}
                            </PlayPauseButton>
                            <TimeDisplay>
                              {formatTime(currentTime)} / {formatTime(duration)}
                            </TimeDisplay>
                          </VideoControlsRow>
                        </VideoControls>
                      </VideoContainer>
                    </VideoWrap>

                    {reelDetail.reel.description && (
                      <ReelDescription>{reelDetail.reel.description}</ReelDescription>
                    )}

                    <SectionTitle>
                      Comments ({reelDetail.comments.length})
                    </SectionTitle>

                    <DetailCommentList>
                      {reelDetail.comments.map((comment) => (
                        <DetailCommentRow key={comment._id}>
                          <Avatar
                            url={comment.byUserProfilePhotoUrl}
                            name={comment.byUsername}
                            sx={{ width: 28, height: 28 }}
                            onClick={() => navigate(`/user/${comment.byUserId}`)}
                          />
                          <DetailCommentBody>
                            <DetailCommentHead>
                              <UserName
                                type="button"
                                $strong
                                title="Open user"
                                onClick={() => navigate(`/user/${comment.byUserId}`)}
                              >
                                {comment.byUsername}
                              </UserName>
                              <CommentMeta>
                                {timeSinceString(comment.created)} ago
                              </CommentMeta>
                            </DetailCommentHead>
                            <DetailCommentText>{comment.text}</DetailCommentText>
                          </DetailCommentBody>
                          <Tooltip title="Delete comment">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete comment"
                              onClick={() => handleDeleteClick(comment._id)}
                            >
                              <TrashIcon width={16} />
                            </IconButton>
                          </Tooltip>
                        </DetailCommentRow>
                      ))}
                    </DetailCommentList>
                  </>
                )}
              </DetailBody>
            </PaneScroll>
          </DetailPanel>
        )}
      </PageContainer>

      {showDeleteConfirm && (
        <DeleteConfirmationOverlay onClick={handleCancelDelete}>
          <DeleteConfirmationContent onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Delete comment</ConfirmTitle>
            <ConfirmText>
              Are you sure you want to delete this comment? This action cannot be
              undone.
            </ConfirmText>
            <DeleteConfirmationButtons>
              <ConfirmButton variant="cancel" onClick={handleCancelDelete}>
                Cancel
              </ConfirmButton>
              <ConfirmButton variant="danger" onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </DeleteConfirmationButtons>
          </DeleteConfirmationContent>
        </DeleteConfirmationOverlay>
      )}
    </Page>
  );
};

export default ReelComments;
